import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  EXPORT_ACTION,
  EXPORT_ACTION_GRAPHQL_NAME,
  EXPORT_REASON_SURVEY_FEATURE_FLAG,
  ExportAction,
  ExportSource,
  GA_CATEGORY,
  SURVEYED_SOURCES,
} from 'components/exportReasonSurvey/constants';
import { EXPORT_REASON_SURVEY_SHOULD_ASK } from 'components/exportReasonSurvey/graphql';
import client from 'graphql/client';
import { all, call, put, select, spawn, takeEvery } from 'redux-saga/effects';
import analyticsActions from 'redux/analytics/actions';
import forwardActions from 'redux/forwardApplications/actions';
import spreadsheetActions from 'redux/spreadsheet/actions';
import zipActions from 'redux/zip/actions';
import { ReduxAction } from 'types/redux.types';

type SliceState = {
  open: boolean;
  action: ExportAction | null;
  applicationCount: number;
  projectId: number | null;
  /**
   * Where the export currently under way was started from, per action. The completed action says
   * what was exported but not who asked for it, and each of the three send modals is a singleton,
   * so the latest trigger for an action is the one that finishes.
   */
  pendingSource: Partial<Record<ExportAction, ExportSource>>;
};

const initialState: SliceState = {
  open: false,
  action: null,
  applicationCount: 0,
  projectId: null,
  pendingSource: {},
};

const exportReasonSurveySlice = createSlice({
  name: 'exportReasonSurvey',
  initialState,
  reducers: {
    ask: (
      state,
      action: PayloadAction<Pick<SliceState, 'action' | 'applicationCount' | 'projectId'>>
    ) => {
      state.open = true;
      state.action = action.payload.action;
      state.applicationCount = action.payload.applicationCount;
      state.projectId = action.payload.projectId;
    },
    /** Dispatched by whichever control opened the send modal, before anything is exported. */
    exportTriggered: (
      state,
      action: PayloadAction<{ action: ExportAction; source: ExportSource }>
    ) => {
      state.pendingSource[action.payload.action] = action.payload.source;
    },
    close: (state) => {
      state.open = false;
      state.action = null;
      state.applicationCount = 0;
      state.projectId = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase('LOGOUT', () => initialState);
  },
});

// saga

/** The export each completed action stands for. Anything not listed is not part of the study. */
const ACTION_BY_DONE_TYPE: Record<string, ExportAction> = {
  [zipActions.ZIP_APPLICATIONS_DONE]: EXPORT_ACTION.zip,
  [spreadsheetActions.SPREADSHEET_GENERATE_DONE]: EXPORT_ACTION.spreadsheet,
  [forwardActions.FORWARD_EMAIL_DONE]: EXPORT_ACTION.forward,
};

const shouldAskGraphql = (exportAction: ExportAction) =>
  client.query({
    query: EXPORT_REASON_SURVEY_SHOULD_ASK,
    variables: { action: EXPORT_ACTION_GRAPHQL_NAME[exportAction] },
    // The answer changes the moment anyone answers, so a cached yes would ask twice.
    fetchPolicy: 'network-only',
  });

function* maybeAskGenerator(action: ReduxAction) {
  const exportAction = ACTION_BY_DONE_TYPE[action.type as string];

  if (!exportAction) {
    return;
  }

  // @ts-ignore - the root reducer is plain JS, so the store shape isn't inferred here.
  const alreadyOpen = yield select((state) => state.ExportReasonSurvey.open);
  // @ts-ignore
  const flags = yield select((state) => state.FeatureFlags.flags);
  // @ts-ignore
  const source = yield select((state) => state.ExportReasonSurvey.pendingSource[exportAction]);

  if (alreadyOpen || !flags?.includes(EXPORT_REASON_SURVEY_FEATURE_FLAG)) {
    return;
  }

  // An unknown source means the export was started somewhere that doesn't report one yet, which
  // is not something to guess about.
  if (!source || !SURVEYED_SOURCES.includes(source)) {
    return;
  }

  const applicationCount = action.payload?.applicationIds?.length ?? 0;

  if (!applicationCount) {
    return;
  }

  // @ts-ignore
  const projectId = yield select((state) => state.Project.get('currentProject')?.get('id') ?? null);

  try {
    // @ts-ignore
    const response = yield call(shouldAskGraphql, exportAction);

    if (!response.data?.exportReasonSurveyShouldAsk) {
      return;
    }
  } catch (e) {
    // A study that can't reach the server is not worth a visible failure - the export itself
    // already succeeded, which is the part the employer asked for.
    console.error('Export reason survey check failed', e);
    return;
  }

  yield put(
    actions.ask({
      action: exportAction,
      applicationCount,
      projectId: projectId ? Number(projectId) : null,
    })
  );
  yield put(analyticsActions.reduxTrackEvent(GA_CATEGORY, 'shown', exportAction));
}

export function* watchExports() {
  yield takeEvery(Object.keys(ACTION_BY_DONE_TYPE), maybeAskGenerator);
}

export function* rootSaga() {
  yield all([spawn(watchExports)]);
}

// exports

const { actions, reducer } = exportReasonSurveySlice;
export const { ask, close, exportTriggered } = actions;
export default reducer;
